import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Small presentational helpers shared by every Investigation Drawer tab
 * (attachments, URLs, …). Promoted out of the attachment drawer so both features
 * render identical section / row / related-row / empty affordances.
 */
export function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{icon}{title}</h4>
      {children}
    </section>
  );
}

export function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2 py-0.5">
      <dt className="w-24 shrink-0 text-fg-muted">{label}</dt>
      <dd className={cn('min-w-0 flex-1 break-all text-fg', mono && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}

/**
 * A bordered, non-interactive list row (the drawers' standard "fact row").
 * Use inside a `<ul>`; for clickable rows use `NavRow` wrapped in an `<li>`.
 */
export function InfoRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <li className={cn('flex items-center justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-sm', className)}>
      {children}
    </li>
  );
}

/** A bordered related-entity row; interactive (a real button) when `onClick` is given. */
export function NavRow({ onClick, children }: { onClick?: () => void; children: ReactNode }) {
  if (!onClick) {
    return <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-sm">{children}</div>;
  }
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-1.5 text-left text-sm hover:bg-surface-raised/50">
      {children}
    </button>
  );
}

export function Empty({ label = 'None recorded.' }: { label?: string }) {
  return <p className="rounded-md border border-dashed border-border px-3 py-2 text-xs text-fg-subtle">{label}</p>;
}
